import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Card } from '@startengine/design-system.ui.card';
import PropTypes from 'prop-types';
import { useState } from 'react';
import ShareIcon from '@mui/icons-material/Share';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import useDayjs from 'hooks/useDayjs';
import useIsMobile from 'hooks/useIsMobile';
import { autoEmbedVideos } from './autoEmbedVideos';
import stripFroalaEditorChrome from 'utils/sanitizeFroalaHtml';

const Update = ({ update = {}, loading = false, isTwoDaysBefore = false }) => {
  const isMobile = useIsMobile();
  const dayjs = useDayjs();
  const [openSnackbar, setOpenSnackbar] = useState(null);

  const fallbackCopyToClipboard = (text) => {
    try {
      // Create a text area element and set its value to the given text
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);

      textArea.select();

      // Copy the text to the clipboard
      const successful = document.execCommand('copy');

      document.body.removeChild(textArea);
      return successful;
    } catch {
      // Ignore errors
      return false;
    }
  };

  const handleShareClick = () => {
    // Create a URL for the specific update
    const url = update?.id
      ? `${window.location.origin}/offering/${update.offering?.slug || window.location.pathname.split('/')[2]}/update/${update.id}`
      : `${window.location.href.split('#')[0]}#updates-section`;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setOpenSnackbar(true);
        })
        .catch(() => {
          const result = fallbackCopyToClipboard(url);
          setOpenSnackbar(result);
        });
    } else {
      const result = fallbackCopyToClipboard(url);
      setOpenSnackbar(result);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(null);
  };

  return (
    <div id={update?.id ? `update-${update.id}` : undefined} data-cy="update-container">
      <Card
        content={
          <Grid container direction="column" spacing={isMobile ? 0 : 3} px={3} pb={3} pt="40px">
            {loading ? (
              <Grid
                item
                data-testid="loading-skeleton-container"
                data-cy="loading-skeleton-container"
              >
                <Skeleton height={40} width="100%" data-testid="skeleton-item" />
                <Skeleton height={40} width="100%" data-testid="skeleton-item" />
                <Skeleton height={40} width="100%" data-testid="skeleton-item" />
              </Grid>
            ) : (
              <>
                <Grid
                  item
                  sx={{
                    paddingTop: '0 !important',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant={isMobile ? 'p1' : 'p1Bold'}
                    color={isTwoDaysBefore && '#E06004'}
                    data-cy="update-date"
                  >
                    {dayjs(update?.deployedAt || update?.scheduledAt || update?.approvedAt).format(
                      'MM.DD.YY',
                    )}
                  </Typography>
                  {!loading && (
                    <Tooltip title="Share Update">
                      <IconButton
                        onClick={handleShareClick}
                        size="small"
                        aria-label="share"
                        data-cy="share-update-button"
                      >
                        <ShareIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
                <Grid item pt="0 !important" py={1}>
                  <Typography
                    variant={isMobile ? 'h5' : 'h3'}
                    color={isTwoDaysBefore && '#E06004'}
                    data-cy="update-title"
                  >
                    {update?.title}
                  </Typography>
                </Grid>
                <Grid
                  item
                  sx={{
                    display: 'flex',
                    alignSelf: 'center',
                    width: '100%',
                  }}
                >
                  <Box
                    data-cy="update-content"
                    dangerouslySetInnerHTML={{
                      __html: autoEmbedVideos(stripFroalaEditorChrome(update?.content)),
                    }}
                    sx={{
                      wordBreak: 'break-word',
                      width: '100%',
                      '*': {
                        whiteSpace: 'normal !important',
                      },
                      maxWidth: '100%',
                      pre: {
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                      },
                      video: {
                        maxWidth: '100%',
                        height: 'auto',
                      },
                      iframe: {
                        maxWidth: '100%',
                        minWidth: {
                          xs: '100%',
                          lg: 640,
                        },
                      },
                      img: {
                        maxWidth: '100%',
                        height: 'auto',
                      },
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '20px',
                      letterSpacing: '0.05px',
                      '[data-qa="virtual-list-item"]': {
                        display: 'none !important',
                      },
                      'span[fr-original-class="fr-video fr-deletable fr-rv fr-draggable"]': {
                        position: 'relative',
                      },
                      position: 'relative',
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        }
      />
      <Snackbar
        open={openSnackbar === true}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        data-cy="success-snackbar"
        data-testid="success-snackbar"
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>
      <Snackbar
        open={openSnackbar === false}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        data-cy="error-snackbar"
        data-testid="error-snackbar"
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          Failed to copy link!
        </Alert>
      </Snackbar>
    </div>
  );
};

Update.propTypes = {
  update: PropTypes.object,
  loading: PropTypes.bool,
  isTwoDaysBefore: PropTypes.bool,
};

export default Update;
